import { useContext, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import styled from "styled-components";
import LayoutContext from "../../../LayoutContext";
import * as Library from "../../../utils/Library";
import BOStageXCards from "../../common/gridCards/BOStageXCards";
import FullScreenLoader from "../../common/loaders/FullScreenLoader";
import MainWindowHeader from "../../common/mainApp/mainWindowHeaders/MainWindowHeader";
import { APP_TITLE } from "../../../config/constant";
import { stockPageCardData } from "../../../config/apiPages";
import { apiActions } from "../apiPage/apiPageHelper";

// #region SCOPED CSS
const StyledPage = styled.div`
    & {
        display: flex;
        flex-direction: column;
        width: 100%;
        height: 100%;
        justify-content: center;
    }

    .frame-208,
    .frame-208 * {
        box-sizing: border-box;
    }
    .frame-208 {
        display: flex;
        flex-direction: column;
        gap: 24px;
        align-items: flex-start;
        justify-content: center;
        position: relative;
    }
    .frame-175 {
        //padding: 0px 300px 0px 300px;
        display: flex;
        flex-direction: row;
        gap: 24px;
        align-items: flex-start;
        justify-content: center;
        flex-shrink: 0;
        width: 100%;
        height: 193px;
        position: relative;
    }
`;
// #endregion

export default function StockPage() {
    document.title = `${APP_TITLE} - Stock`;

    // #region VARS
    const layoutCtx = useContext(LayoutContext);
    const { layoutType } = layoutCtx;
    const navigations = useSelector(state => state.navigationAccesses);
    const navigate = useNavigate();

    const [allCardData, setAllCardData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    //---------- Header Filters
    const [allPOSList, setAllPOSList] = useState([]);
    const [allEnvironmentsList, setAllEnvironmentsList] = useState([]);
    //----------
    const [selectedPOS, setSelectedPOS] = useState(undefined);
    const [selectedEnvironment, setSelectedEnvironment] = useState(undefined);
    // #endregion

    // #region METHODS
    const getAllCardData = async () => {
        // get all POSs
        const posListRaw = JSON.parse(localStorage.getItem("POSs")) || [];
        const allPOS = posListRaw?.map(pos => ({
            value: pos?._id,
            name: pos?.name
        }));
        setAllPOSList(allPOS);
        setSelectedPOS(allPOS.length > 0 ? [{ value: allPOS[0].value, name: allPOS[0].name }] : []);

        // get all environments
        let allEnvironments = posListRaw?.[0]?.environments;
        allEnvironments = allEnvironments?.map(env => ({
            value: env,
            name: env
        }));
        setAllEnvironmentsList(allEnvironments);
        setSelectedEnvironment(
            allEnvironments.length > 0 ? [{ value: allEnvironments[0].value, name: allEnvironments[0].name }] : []
        );

        const posID = allPOS?.[0]?.value;
        const environmentName = allEnvironments?.[0]?.value;
        const APIs = stockPageCardData.map(card => card?.api);

        const apiData = await getAPIsDataRequest(posID, environmentName, APIs);

        const updatedCardData = stockPageCardData.map(card => {
            const apiStatus = apiData?.[card?.api]?.[0]?.status;
            if (typeof apiStatus === "string" && apiStatus) {
                return {
                    ...card,
                    type: "edit",
                    status: apiStatus.toUpperCase()
                };
            }
            return card;
        });

        setAllCardData(updatedCardData);
    };

    const getAPIsDataRequest = async (posID, environmentName, APIs) => {
        try {
            const formData = {
                posID: posID,
                environmentName: environmentName,
                APIs: APIs
            };
            const response = await Library.makePostRequest("getAPIsData", formData, false, setIsLoading);
            if (response?.data?.status !== 200) return;

            const apiData = response?.data?.data?.apiData;

            return apiData;
        } catch (error) {
            console.error("Error fetching card data:", error);
        }
    };

    const onPOSChange = pos => {
        setSelectedPOS([pos]);
    };

    const onEnvironmentChange = environment => {
        setSelectedEnvironment([environment]);
    };

    const handleCardActionClick = cardData => {
        const posID = selectedPOS?.[0]?.value;
        const environmentName = selectedEnvironment?.[0]?.value;
        const apiName = cardData.api;
        const type = cardData.type === "add" ? apiActions.CREATE : apiActions.EDIT;

        navigate(
            `/stock/api/${type}?posID=${posID}&environmentName=${environmentName}&apiName=${apiName}&posName=${selectedPOS?.[0]?.name}`
        );
    };
    // #endregion

    // #region EFFECTS
    useEffect(() => {
        getAllCardData();
    }, []);

    useEffect(() => {
        setupHeaders({
            layoutCtx,
            onClickRefreshAction: getAllCardData,
            navigations,
            allPOSList,
            allEnvironmentsList,
            onPOSChange,
            onEnvironmentChange,
            selectedPOS,
            selectedEnvironment
        });
    }, [layoutType, navigations, allPOSList, allEnvironmentsList, selectedPOS, selectedEnvironment]);

    // #endregion

    // #region MEMOIZED VARS
    const rows = useMemo(() => {
        // Split cards into rows of max 3 per row
        const result = [];
        for (let i = 0; i < allCardData?.length; i += 3) {
            result.push(allCardData?.slice(i, i + 3));
        }
        return result;
    }, [allCardData]);
    // #endregion

    return (
        <StyledPage className="pos-setup-page">
            <FullScreenLoader isLoading={isLoading} />

            <div className="frame-208">
                {rows.map((row, rowIdx) => (
                    <div className="frame-175" key={rowIdx}>
                        {row.map((card, cardIdx) => (
                            <BOStageXCards
                                key={`${rowIdx}-${cardIdx}`}
                                title={card?.title}
                                type={card?.type}
                                status={card?.status}
                                onClick={() => handleCardActionClick(card)}
                            />
                        ))}
                    </div>
                ))}
            </div>
        </StyledPage>
    );
}

// #region SMALL COMPONENTS
function setupHeaders({
    layoutCtx,
    onClickRefreshAction,
    navigations,
    allPOSList,
    allEnvironmentsList,
    onPOSChange,
    onEnvironmentChange,
    selectedPOS,
    selectedEnvironment
}) {
    const createMode = navigations?.navigationAccesses?.fulfillmentCenters?.createModeOnly;

    const {
        setLayoutType,
        setControlBarLeftBtns,
        setControlBarCenterBtns,
        setControlBarRightBtns,
        setFilterHeaderProperties
    } = layoutCtx;
    if (!setFilterHeaderProperties) return;

    setControlBarLeftBtns([]);
    setControlBarCenterBtns([{ to: "#", active: true, disabled: false, title: "STOCK" }]);
    setControlBarRightBtns([
        { to: "#", active: true, disabled: createMode, title: "View", icon: "eye" },
        { to: "#", active: false, disabled: true, title: "Add", icon: "plus" },
        { to: "#", active: false, disabled: true, title: "Details", icon: "microscope" }
    ]);

    setFilterHeaderProperties({
        customContentHeader: (
            <MainWindowHeader
                key={window.location.pathname}
                visible={true}
                layoutTypeOptions={{
                    gridEnabled: true,
                    listEnabled: false
                }}
                onRefreshClick={onClickRefreshAction}
                firstOrganize={{
                    visible: true,
                    dropdownData: allPOSList,
                    onDropdownChange: onPOSChange,
                    disabled: false,
                    type: "singleSelect",
                    selectedValue: selectedPOS?.[0]?.name
                }}
                secondOrganize={{
                    visible: true,
                    dropdownData: allEnvironmentsList,
                    onDropdownChange: onEnvironmentChange,
                    type: "singleSelect",
                    selectedValue: selectedEnvironment?.[0]?.name
                }}
                pageFilters={{
                    useFilters: false
                }}
            />
        )
    });
}
// #endregion
